import { matrixAxios } from "@/api/axios";

// Matrix API Response Types
interface MatrixJoinedRoomsResponse {
	joined_rooms: string[];
}

interface MatrixRoomMembersResponse {
	chunk: Array<{
		state_key: string;
		[key: string]: any;
	}>;
}

interface MatrixCreateRoomResponse {
	room_id: string;
}

interface MatrixDevice {
	device_id: string;
	display_name?: string;
	last_seen_ip?: string;
	last_seen_ts?: number;
}

interface MatrixMessage {
	content: {
		body: string;
		format?: string;
		formatted_body?: string;
		msgtype: string;
		[key: string]: any;
	};
	event_id: string;
	origin_server_ts: number;
	room_id: string;
	sender: string;
	type: string;
	unsigned?: {
		age?: number;
		membership?: string;
		prev_content?: any;
		redacted_because?: any;
		transaction_id?: string;
	};
}

interface MatrixRoomMessages {
	chunk: MatrixMessage[];
	start?: string;
	end?: string;
	next_batch?: string;
}

export class MatrixHttpClient {
	private homeServerUrl: string;
	private accessToken: string;
	private userId: string;

	constructor(homeServerUrl: string, accessToken: string, userId: string) {
		// Ensure homeServerUrl has a protocol prefix
		this.homeServerUrl = homeServerUrl.startsWith("http") ? homeServerUrl : `https://${homeServerUrl}`;
		this.accessToken = accessToken;
		this.userId = userId;

		// Set base URL for Matrix API
		matrixAxios.defaults.baseURL = `${this.homeServerUrl}/_matrix/client/v3`;
	}

	private async request<T>(endpoint: string, options: { method?: string; data?: any } = {}): Promise<T> {
		const response = await matrixAxios<T>({
			url: endpoint,
			method: options.method || "GET",
			data: options.data,
			headers: {
				Authorization: this.accessToken
			}
		});
		return response.data;
	}

	getUserId() {
		return this.userId;
	}

	// Room State
	async getRoomName(roomId: string): Promise<string | null> {
		try {
			const res = await this.request<{ name?: string }>(`/rooms/${roomId}/state/m.room.name`);
			return res?.name || null;
		} catch {
			return null;
		}
	}

	// Room Methods
	async getJoinedRooms() {
		return this.request<MatrixJoinedRoomsResponse>("/joined_rooms");
	}

	async getRoomMembers(roomId: string) {
		return this.request<MatrixRoomMembersResponse>(`/rooms/${roomId}/members`);
	}

	async createRoom(inviteUserIds: string[], name: string) {
		return this.request<MatrixCreateRoomResponse>("/createRoom", {
			method: "POST",
			data: {
				invite: inviteUserIds,
				name,
				preset: "private_chat"
			}
		});
	}

	async joinRoom(roomId: string, reason?: string) {
		return this.request(`/rooms/${roomId}/join`, {
			method: "POST",
			data: { reason }
		});
	}

	async leaveRoom(roomId: string, reason?: string) {
		return this.request(`/rooms/${roomId}/leave`, {
			method: "POST",
			data: { reason }
		});
	}

	// Message Methods
	async getRoomMessages(roomId: string, options: {
		from?: string;
		to?: string;
		dir?: "b" | "f";
		limit?: number;
		filter?: string;
	} = {}) {
		const params = new URLSearchParams();
		if (options.from)
			params.append("from", options.from);
		if (options.to)
			params.append("to", options.to);
		if (options.dir)
			params.append("dir", options.dir);
		if (options.limit)
			params.append("limit", options.limit.toString());
		if (options.filter)
			params.append("filter", options.filter);

		return this.request<MatrixRoomMessages>(`/rooms/${roomId}/messages?${params.toString()}`);
	}

	async getRecentMessages(roomId: string, limit: number = 50) {
		return this.getRoomMessages(roomId, { limit, dir: "b" });
	}

	async sendMessage(roomId: string, message: string, msgType: string = "m.text") {
		const txnId = Date.now().toString();
		return this.request(`/rooms/${roomId}/send/m.room.message/${txnId}`, {
			method: "PUT",
			data: {
				msgtype: msgType,
				body: message
			}
		});
	}

	async sendFormattedMessage(roomId: string, text: string, formattedText: string) {
		const txnId = Date.now().toString();
		return this.request(`/rooms/${roomId}/send/m.room.message/${txnId}`, {
			method: "PUT",
			data: {
				msgtype: "m.text",
				body: text,
				formatted_body: formattedText,
				format: "org.matrix.custom.html"
			}
		});
	}

	// User Profile Methods
	async getUserProfile(userId?: string) {
		return this.request(`/profile/${userId || this.userId}`);
	}

	async setDisplayName(displayName: string) {
		return this.request(`/profile/${this.userId}/displayname`, {
			method: "PUT",
			data: { displayname: displayName }
		});
	}

	// Device Methods
	async getDevices() {
		return this.request<MatrixDevice[]>("/devices");
	}

	async getDevice(deviceId: string) {
		return this.request(`/devices/${deviceId}`);
	}

	async updateDevice(deviceId: string, displayName: string) {
		return this.request(`/devices/${deviceId}`, {
			method: "PUT",
			data: { display_name: displayName }
		});
	}

	async deleteDevice(deviceId: string, authData?: any) {
		return this.request(`/devices/${deviceId}`, {
			method: "DELETE",
			data: authData ? { auth: authData } : undefined
		});
	}

	// Typing Notifications
	async sendTypingNotification(roomId: string, isTyping: boolean, timeoutMs: number = 30000) {
		return this.request(`/rooms/${roomId}/typing/${this.userId}`, {
			method: "PUT",
			data: {
				typing: isTyping,
				timeout: timeoutMs
			}
		});
	}

	// Read Receipts
	async markRoomAsRead(roomId: string, eventId: string) {
		return this.request(`/rooms/${roomId}/receipt/m.read/${eventId}`, {
			method: "POST"
		});
	}

	// Direct Messages
	async getDirectMessageRooms() {
		return this.request("/account/data/m.direct");
	}
}

interface MatrixUser {
	matrixAccessToken?: string;
	matrixUserId?: string;
	matrixHomeServer?: string;
}

interface MatrixVendor {
	matrixUserId?: string;
	fullName?: string;
}

export async function initializeChat(
	currentUser: MatrixUser,
	vendor: MatrixVendor,
	onError: (message: string) => void,
	onSuccess: (roomId: string) => void
) {
	try {
		// Get Matrix credentials
		const accessToken = currentUser.matrixAccessToken;
		const userId = currentUser.matrixUserId;
		const homeServerUrl = currentUser.matrixHomeServer || import.meta.env.VITE_MATRIX_HOME_SERVER || "https://chat.api.intasida.com";

		if (!accessToken || !userId) {
			throw new Error("Matrix credentials not found. Please contact support.");
		}

		const vendorMatrixUserId = vendor.matrixUserId;
		if (!vendorMatrixUserId) {
			throw new Error("Vendor matrix user ID not found. Please contact support.");
		}

		// Create Matrix HTTP client
		const matrixClient = new MatrixHttpClient(homeServerUrl, accessToken, userId);

		// Get all joined rooms
		const { joined_rooms } = await matrixClient.getJoinedRooms();

		// Check if we already have a room with this vendor
		let existingRoomId: string | null = null;

		for (const roomId of joined_rooms) {
			try {
				const { chunk: roomMembers } = await matrixClient.getRoomMembers(roomId);

				// Check if vendor is a member of this room
				const vendorInRoom = roomMembers.some(
					member => member.state_key === vendorMatrixUserId
				);

				if (vendorInRoom) {
					existingRoomId = roomId;
					break;
				}
			}
			catch (error) {
				console.error(`Error checking room ${roomId}:`, error);
				continue; // Skip this room and check the next one
			}
		}

		let roomId: string;

		if (existingRoomId) {
			// Use existing room
			roomId = existingRoomId;
		}
		else {
			// Create new room with vendor
			const { room_id } = await matrixClient.createRoom(
				[vendorMatrixUserId],
				`Chat with ${vendor.fullName || "Vendor"}`
			);
			roomId = room_id;
			console.error("Created new room with vendor:", roomId);
		}

		onSuccess(roomId);
	}
	catch (error: any) {
		console.error("Error starting chat:", error);
		onError(error.message || "Could not start chat. Please try again.");
	}
}
